import {
  GetJobProgressionRequest,
  SetAuthorizationRequestedProgressionRequest,
  SetAuthorizationSignedProgressionRequest,
  SetDraftApprovedProgressionRequest,
  SetDraftInReviewProgressionRequest,
  SetDraftRejectedProgressionRequest,
  OverrideJobProgressionStepRequest,
  SetJobStartedProgressionRequest,
  SetOpenItemsProgressionRequest,
  SetReturnFiledProgressionRequest,
  OverrideableJobProgressionStepStatus as OverrideableJobProgressionStepStatusProto,
  SetDeliverableCompletedProgressionRequest,
  SetPreparationCompletedProgressionRequest,
  SetReviewCompletedProgressionRequest,
  SetExtensionInProgressProgressionRequest,
  SetExtensionConfirmedProgressionRequest,
  SetExtensionAcceptedProgressionRequest,
  SetExtensionRejectedProgressionRequest,
  SetReadyToFileProgressionRequest,
  JobProgressionExtensionType,
  EstimatedTaxPaymentInput,
  EstimatedTaxPaymentStateInput,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { numberToDecimal } from 'utils/grpc-util'
import { collection } from 'infra/collection'
import { action } from 'mobx'
import { Id } from 'types/definitions'
import { JobProgressionAPI } from './API'
import { JobProgressionDto } from './dtos'
import {
  ExtensionProgressionFlow,
  JobProgression,
  OverrideableJobProgressionStepStatus,
} from './JobProgression'
import { match } from 'utils/pattern-match-util'
import { map } from 'rxjs'

interface EstimatedTaxPayment {
  federalAmount: number
  amountByState: { state: string; amount: number }[]
  isCalculated: boolean
}

/**
 * Progression prime delegate
 */
export type PrimeProgression = (primeProps: {
  jobId?: string
}) => JobProgression

export default class JobProgressionStore {
  constructor(private api: JobProgressionAPI) {}

  progressions = collection<JobProgressionDto, JobProgression>({
    create: () => new JobProgression(),
    getModelId: (p) => p.jobId,
    getDataId: (p) => p.jobId,
  })

  /**
   * Prime singular job progression step.
   *
   * @param props
   */
  readonly prime: PrimeProgression = ({ jobId }) => {
    const existing = this.progressions.get(jobId)
    if (existing) {
      return existing
    }
    return this.progressions.set({ jobId })
  }

  /**
   * Job progression events from the realtime channel.
   */
  readonly events$ = this.api.events$.pipe(
    map((x) => x.getJobProgression()?.toObject().jobId),
  )

  /**
   * Looks up progression steps by job ID.
   */
  progressionForJob = (jobId?: string) =>
    this.progressions.find((p) => p.jobId === jobId)

  /**
   * Lists all progression steps.
   * @param jobId
   * @returns
   */
  async fetchProgression(jobId: Id) {
    const request = new GetJobProgressionRequest()
    request.setJobId(Id.assert(jobId))

    const response = await this.api.getProgression(request)
    const obj = response.toObject()

    this.progressions.set({
      id: obj.id,
      jobId,
      flow: obj.progressionFlow,
      steps: obj.progressionStepsList,
    })
  }

  /**
   * Sets the current progression to jobp started.
   * @param jobId
   */
  async setOpenItemsProgression(jobId: Id) {
    const request = new SetOpenItemsProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setOpenItemsProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to job started.
   * @param jobId
   */
  @action.bound
  async setJobStartedProgression(jobId: Id) {
    const request = new SetJobStartedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setJobStartedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to draft in review.
   * @param jobId
   */
  async setDraftInReviewProgression(jobId: Id) {
    const request = new SetDraftInReviewProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setDraftInReviewProgression(request)
    await this.fetchProgression(jobId)
  }

  async setPreparationJobCompleted(jobId: Id) {
    const request = new SetPreparationCompletedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setPreparationJobCompleted(request)
    await this.fetchProgression(jobId)
  }

  async setReviewJobCompleted(jobId: Id) {
    const request = new SetReviewCompletedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setReviewJobCompleted(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to draft approved
   */
  async setDraftApprovedProgression(jobId: Id) {
    const request = new SetDraftApprovedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setDraftApprovedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to draft rejected
   */
  async setDraftRejectedProgression(jobId: Id) {
    const request = new SetDraftRejectedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setDraftRejectedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to authorization requested
   */
  async setAuthorizationRequestedProgression(jobId: Id) {
    const request = new SetAuthorizationRequestedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setAuthorizationRequestedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to authorization signed
   */
  async setAuthorizationSignedProgression(jobId: Id) {
    const request = new SetAuthorizationSignedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setAuthorizationSignedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to return filed
   */
  async setReturnFiledProgression(jobId: Id) {
    const request = new SetReturnFiledProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setReturnFiledProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to deliverable completed
   */
  async setDeliverableCompletedProgression(jobId: Id) {
    const request = new SetDeliverableCompletedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setDeliverableCompletedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to extension in progress.
   * @param jobId
   */
  @action.bound
  async SetExtensionInProgressProgression(
    jobId: Id,
    progressionFlow: ExtensionProgressionFlow,
  ) {
    const request = new SetExtensionInProgressProgressionRequest()
    request.setJobId(Id.assert(jobId))
    switch (progressionFlow) {
      case ExtensionProgressionFlow.PERSONAL:
        request.setExtensionType(JobProgressionExtensionType.PERSONAL)
        break
      case ExtensionProgressionFlow.BUSINESS:
        request.setExtensionType(JobProgressionExtensionType.BUSINESS)
        break
      case ExtensionProgressionFlow.OUTSOURCING:
        request.setExtensionType(
          JobProgressionExtensionType.OUTSOURCING_EXTENSION_TYPE,
        )
        break
      default:
        throw new Error('Invalid progression flow')
    }

    await this.api.setExtensionInProgressProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to extension confirmed.
   * @param jobId
   */
  @action.bound
  async SetExtensionConfirmedProgression(jobId: Id) {
    const request = new SetExtensionConfirmedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setExtensionConfirmedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to extension accepted.
   * @param jobId
   * @param estimatedTaxPayment
   */
  @action.bound
  async setExtensionAcceptedProgression(
    jobId: Id,
    estimatedTaxPayment?: EstimatedTaxPayment,
  ) {
    const request = new SetExtensionAcceptedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    if (estimatedTaxPayment) {
      const input = new EstimatedTaxPaymentInput()
      input.setFederalAmount(numberToDecimal(estimatedTaxPayment.federalAmount))
      input.setAmountByStateList(
        estimatedTaxPayment.amountByState.map((s) =>
          new EstimatedTaxPaymentStateInput()
            .setState(s.state)
            .setAmount(numberToDecimal(s.amount)),
        ),
      )
      input.setIsCalculated(estimatedTaxPayment.isCalculated)
      request.setEstimatedTaxPayment(input)
    }

    await this.api.setExtensionAcceptedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to extension rejected.
   * @param jobId
   */
  @action.bound
  async SetExtensionRejectedProgression(jobId: Id) {
    const request = new SetExtensionRejectedProgressionRequest()
    request.setJobId(Id.assert(jobId))

    await this.api.setExtensionRejectedProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to ready to file.
   * @param jobId
   */
  @action.bound
  async SetReadyToFileProgression(jobId: Id, setAsAdmin: boolean = false) {
    const request = new SetReadyToFileProgressionRequest()
    request.setJobId(Id.assert(jobId))
    request.setSetAsAdmin(setAsAdmin)

    await this.api.setReadyToFileProgression(request)
    await this.fetchProgression(jobId)
  }

  /**
   * Sets the current progression to specific step
   */
  async overrideProgressionStep(
    jobId: Id,
    stepId: OverrideableJobProgressionStepStatus,
  ) {
    const request = new OverrideJobProgressionStepRequest()
      .setJobId(Id.assert(jobId))
      .setProgressionStep(this.toOverrideableProgressionStepDto(stepId))

    await this.api.overrideJobProgressionStep(request)
    await this.fetchProgression(jobId)
  }

  toOverrideableProgressionStepDto(
    stepId: OverrideableJobProgressionStepStatus,
  ) {
    return match(stepId, {
      [OverrideableJobProgressionStepStatus.RETURN_FILED]: () =>
        OverrideableJobProgressionStepStatusProto.ADMIN_RETURN_FILED,
      [OverrideableJobProgressionStepStatus.JOB_STARTED]: () =>
        OverrideableJobProgressionStepStatusProto.ADMIN_JOB_STARTED,
      [OverrideableJobProgressionStepStatus.OPEN_ITEMS]: () =>
        OverrideableJobProgressionStepStatusProto.ADMIN_OPEN_ITEMS,
    })
  }
}
